import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import mktPostsService from 'src/services/mktPostsService';
import { MKT_POSTS_DESIGN as d } from './MktPosts.design';

const ACCEPTED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime'];
const MAX_SIZE = 100 * 1024 * 1024;

export default function MktPostMediaUpload({ value, onChange, max = 10 }) {
    const { t } = useTranslation('marketing');
    const { user } = useAuth();
    const inputRef = useRef(null);
    const dragIndexRef = useRef(null);
    const [progress, setProgress] = useState({});
    const [error, setError] = useState('');

    const media = value || [];
    const canAddMore = media.length < max;

    const uploadOne = async (file) => {
        const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const isVideo = file.type.startsWith('video/');
        const localUrl = URL.createObjectURL(file);

        onChange((prev) => [...prev, { _tempId: tempId, url: localUrl, type: isVideo ? 'video' : 'image', mime: file.type, _uploading: true }]);
        setProgress((prev) => ({ ...prev, [tempId]: 0 }));

        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await mktPostsService.uploadMidia(user.clienteId, formData, (evt) => {
                if (!evt.total) return;
                setProgress((prev) => ({ ...prev, [tempId]: Math.round((evt.loaded / evt.total) * 100) }));
            });

            const uploaded = res?.data;
            onChange((prev) => prev.map((m) => (m._tempId === tempId
                ? { url: uploaded.url, type: uploaded.type || (isVideo ? 'video' : 'image'), mime: uploaded.mime }
                : m)));

            if (res?.warnings?.length) {
                setError(res.warnings[0]);
            }
        } catch (err) {
            console.error('Erro ao enviar mídia:', err);
            setError(err?.message || t('posts.media.uploadFailedNamed', { name: file.name }));
            onChange((prev) => prev.filter((m) => m._tempId !== tempId));
        } finally {
            setProgress((prev) => {
                const next = { ...prev };
                delete next[tempId];
                return next;
            });
            URL.revokeObjectURL(localUrl);
        }
    };

    const handleFiles = (files) => {
        setError('');
        const room = max - media.length;
        const list = Array.from(files || []).slice(0, room);

        list.forEach((file) => {
            if (file.size > MAX_SIZE) {
                setError(t('posts.media.fileTooLarge', { name: file.name }));
                return;
            }
            if (!ACCEPTED_MIMES.includes(file.type)) {
                setError(t('posts.media.unsupportedType', { name: file.name }));
                return;
            }
            uploadOne(file);
        });

        if (inputRef.current) inputRef.current.value = '';
    };

    const handleRemove = (tempIdOrUrl) => {
        onChange((prev) => prev.filter((m) => (m._tempId || m.url) !== tempIdOrUrl));
    };

    const handleDragStart = (index) => (e) => {
        dragIndexRef.current = index;
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDropReorder = (index) => (e) => {
        e.preventDefault();
        const from = dragIndexRef.current;
        dragIndexRef.current = null;
        if (from === null || from === index) return;
        onChange((prev) => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(index, 0, moved);
            return next;
        });
    };

    const handleDropFiles = (e) => {
        e.preventDefault();
        if (dragIndexRef.current !== null) return;
        handleFiles(e.dataTransfer.files);
    };

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_MIMES.join(',')}
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
            />

            <div className={d.mediaGrid.grid}>
                {media.map((item, index) => {
                    const key = item._tempId || item.url;
                    const pct = item._tempId ? progress[item._tempId] : undefined;
                    return (
                        <div
                            key={key}
                            className={d.mediaGrid.item}
                            draggable={!item._uploading}
                            onDragStart={handleDragStart(index)}
                            onDragOver={handleDragOver}
                            onDrop={handleDropReorder(index)}
                        >
                            {item.type === 'video' ? (
                                // eslint-disable-next-line jsx-a11y/media-has-caption
                                <video src={`${item.url}#t=0.1`} preload="metadata" className={d.mediaGrid.thumb} />
                            ) : (
                                <img src={item.url} alt="" className={d.mediaGrid.thumb} />
                            )}

                            <span className={d.mediaGrid.typeBadge}>{item.type === 'video' ? t('posts.detail.video') : t('posts.detail.image')}</span>
                            {media.length > 1 && !item._uploading && (
                                <span className={d.mediaGrid.orderBadge}>{index + 1}</span>
                            )}

                            {item._uploading && (
                                <div className={d.mediaGrid.progressOverlay}>
                                    {pct !== undefined ? `${pct}%` : <Iconify icon="eos-icons:loading" width={20} />}
                                </div>
                            )}

                            {!item._uploading && (
                                <button
                                    type="button"
                                    className={d.mediaGrid.removeBtn}
                                    onClick={() => handleRemove(key)}
                                >
                                    <Iconify icon="eva:close-outline" width={14} />
                                </button>
                            )}
                        </div>
                    );
                })}

                {canAddMore && (
                    <div
                        className={d.mediaGrid.dropzone}
                        onClick={() => inputRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDropFiles}
                    >
                        <Iconify icon="eva:cloud-upload-outline" width={22} />
                        <span className="text-[11px] font-medium text-center px-2">{t('posts.media.add')}</span>
                    </div>
                )}
            </div>

            <p className="text-[11px] text-[#a1a1aa] mt-2">
                {t('posts.media.hintExtended')}
                {media.length > 1 && ` ${t('posts.media.reorderCarousel')}`}
            </p>
            {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
        </div>
    );
}
